import { useMemo } from 'react';
import type { FC } from 'react';
import { DateTime as luxon } from 'luxon';

type TimeUnit = 'ms' | 's' | 'm' | 'h' | 'd' | 'mo' | 'y';

interface DeltaTimeProps {
    baseIso?: string;
    targetIso?: string;
    className?: string;
}

const TIME_DIVIDER = {
    '1s': 1000,
    '1m': 60000,
    '1h': 3600000
};

const Delta: FC<DeltaTimeProps> = ({ baseIso, targetIso, className }) => {
    const { signChar, value, unit } = useMemo(() => {
        if (!baseIso) {
            return { sign: '+', value: 0, unit: 's' as TimeUnit };
        }

        const base = luxon.fromISO(baseIso);
        const target = luxon.fromISO(targetIso ?? '');

        if (!base.isValid || !target.isValid) {
            return { sign: '+', value: 0, unit: 's' as TimeUnit };
        }

        const diffMs = target.toMillis() - base.toMillis();
        const sign = diffMs > 0 ? '+' : '-';
        const absMs = Math.abs(diffMs);

        const years = Math.abs(target.diff(base, 'years').years);
        if (years >= 1) {
            return {
                signChar: sign,
                value: Math.round(years),
                unit: 'y'
            };
        }

        const months = Math.abs(target.diff(base, 'months').months);
        if (months >= 1) {
            return {
                signChar: sign,
                value: Math.round(months),
                unit: 'mo'
            };
        }

        const days = Math.abs(target.diff(base, 'days').days);
        if (days >= 1) {
            return {
                signChar: sign,
                value: Math.round(days),
                unit: 'd'
            };
        }

        if (absMs >= TIME_DIVIDER['1h']) {
            return {
                signChar: sign,
                value: Math.round(absMs / TIME_DIVIDER['1h']),
                unit: 'h'
            };
        }

        if (absMs >= TIME_DIVIDER['1m']) {
            return {
                signChar: sign,
                value: Math.round(absMs / TIME_DIVIDER['1m']),
                unit: 'm'
            };
        }

        if (absMs >= TIME_DIVIDER['1s']) {
            const seconds = Math.round(absMs / TIME_DIVIDER['1s']);
            return {
                signChar: sign,
                value: seconds,
                unit: 's'
            };
        }

        return {
            signChar: sign,
            value: absMs,
            unit: 'ms'
        };
    }, [baseIso, targetIso]);

    return value ? (
        <span className={className}>{`${signChar}${value}${unit}`}</span>
    ) : undefined;
};

export default Delta;
